import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { 
  PlayCircle, 
  Briefcase, 
  Instagram, 
  Users, 
  Check, 
  ExternalLink,
  Calendar,
  User,
  Link as LinkIcon,
  MessageCircle,
  CheckCircle,
  ArrowRight,
  HelpCircle,
  Lightbulb,
  Layers,
  Image,
  Settings,
  Twitter,
  Facebook,
  Linkedin,
  Loader2
} from 'lucide-react';
import { getFirebaseConfig, getAppId } from './firebaseConfig';
import { ChatModal } from './components/ChatModal';
import { defaultAvatarDataUrl } from './utils/defaultAvatar';

// Initialize Firebase for LandingPage
const firebaseConfig = getFirebaseConfig();
const landingApp = initializeApp(firebaseConfig, 'landing-page');
const landingDb = getFirestore(landingApp);
const landingAppId = getAppId();

// Helper function to optimize Cloudinary image URLs with transformations
const optimizeImageUrl = (url, width = 150, height = 150, format = 'auto') => {
  if (!url || typeof url !== 'string') return url;
  
  // Check if it's a Cloudinary URL
  if (url.includes('res.cloudinary.com') && url.includes('/image/upload/')) {
    // Check if transformations are already applied
    if (url.includes('/w_') || url.includes('/c_') || url.includes('/q_')) {
      return url; // Already optimized
    }
    
    // Handle Cloudinary URL structure: /image/upload/[v1234567890/]path/to/image
    const uploadMatch = url.match(/\/image\/upload\/(v\d+\/)?/);
    if (!uploadMatch) return url;
    
    const uploadIndex = uploadMatch.index + uploadMatch[0].length;
    const transformations = `w_${width},h_${height},c_fill,q_auto,f_${format}/`;
    
    return url.slice(0, uploadIndex) + transformations + url.slice(uploadIndex);
  }
  
  // For Unsplash URLs, add size parameters if not present
  if (url.includes('images.unsplash.com')) {
    const urlObj = new URL(url);
    if (!urlObj.searchParams.has('w')) {
      urlObj.searchParams.set('w', width.toString());
      urlObj.searchParams.set('h', height.toString());
      urlObj.searchParams.set('fit', 'crop');
      urlObj.searchParams.set('auto', 'format');
    }
    return urlObj.toString();
  }
  
  return url;
};

// Generate srcset for responsive images
const generateSrcSet = (url, baseWidth, baseHeight, sizes = [1, 2, 3]) => {
  if (!url || typeof url !== 'string') return '';
  
  try {
    // For Cloudinary URLs
    if (url.includes('res.cloudinary.com')) {
      return sizes
        .map(scale => {
          const width = baseWidth * scale;
          const height = baseHeight * scale;
          const optimizedUrl = optimizeImageUrl(url, width, height);
          return `${optimizedUrl} ${width}w`;
        })
        .filter(Boolean) // Remove any empty strings
        .join(', ');
    }
    
    // For Unsplash URLs
    if (url.includes('images.unsplash.com')) {
      return sizes
        .map(scale => {
          const width = baseWidth * scale;
          const height = baseHeight * scale;
          const optimizedUrl = optimizeImageUrl(url, width, height);
          return `${optimizedUrl} ${width}w`;
        })
        .filter(Boolean) // Remove any empty strings
        .join(', ');
    }
  } catch (error) {
    console.warn('Error generating srcset:', error);
    return '';
  }
  
  // Fallback for other URLs - no srcset
  return '';
};

const LandingPage = ({ onGetStarted }) => {
  const navigate = useNavigate();
  const typingContainerRef = useRef(null);
  const [username, setUsername] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [demoProfiles, setDemoProfiles] = useState({ sarah: null, nabin: null });
  const [currentProfile] = useState('sarah'); // Always show Sarah's profile
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(true);
  const [showChatModal, setShowChatModal] = useState(false);
  const phoneRef = useRef(null);
  const heroContainerRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [trainingProgress, setTrainingProgress] = useState(78);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const [activeProblemNode, setActiveProblemNode] = useState(null);
  const [activeEcosystemCard, setActiveEcosystemCard] = useState(null);

  // Memoize FAQ Schema (static data, no need to recreate on every render)
  const faqSchema = useMemo(() => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "PerAI.me Frequently Asked Questions",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is a Smart Bio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A Smart Bio is an AI-powered link-in-bio page that goes beyond static links. Unlike traditional bio pages, a Smart Bio includes an AI assistant that can answer questions 24/7, engage with visitors conversationally, and provide information about your work, availability, rates, and more. PerAI.me creates Smart Bios that transform static Instagram bios and Linktree pages into interactive, conversational experiences."
          }
        },
        {
          "@type": "Question",
          "name": "What is PerAI.me?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "PerAI.me is an AI-powered personal bio platform that transforms static Instagram bios and Linktree pages into interactive, conversational experiences. Users create Smart Bios with AI assistants that represent them and can answer questions 24/7. PerAI.me is the leading platform for creating AI-powered link-in-bio pages."
          }
        },
        {
          "@type": "Question",
          "name": "How does PerAI work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "PerAI works by allowing users to create a profile with their name, photo, bio, social links, and knowledge base. The AI learns from this information to answer questions in the user's style and voice. You get a unique URL (perai.me/username) that visitors can chat with anytime. The AI adapts responses based on your profession, tone, and knowledge base. Setup takes 2 minutes and the AI is ready immediately."
          }
        },
        {
          "@type": "Question",
          "name": "Is PerAI free to use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, PerAI offers free profiles. You can create your personal AI twin, customize your profile, add links and knowledge, and start sharing your link immediately at no cost. PerAI.me provides free access to all core features including personal AI, link management, and profile customization."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to set up a Smart Bio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Setup takes approximately 2 minutes. You'll add your basic information (name, photo, bio), connect your social links, and optionally add knowledge base content. The AI is ready to use immediately after setup. No coding or technical skills required."
          }
        },
        {
          "@type": "Question",
          "name": "Who should use PerAI?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "PerAI is perfect for creators, professionals, businesses, real estate agents, educators, and anyone who wants to go beyond static links. It's ideal for people who want to answer common questions automatically, showcase personality, create an interactive digital business card, or provide 24/7 customer support without manual intervention."
          }
        },
        {
          "@type": "Question",
          "name": "Where can I share my PerAI link?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can share your PerAI link anywhere - Instagram bio, WhatsApp, iMessage, email signatures, Twitter bio, LinkedIn, TikTok, or any platform. It works seamlessly across all social media and messaging platforms. The link works in browsers and in-app browsers. PerAI.me links are globally accessible and work on all devices."
          }
        },
        {
          "@type": "Question",
          "name": "Can I add my personal AI to my Instagram bio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! PerAI.me is perfect for Instagram bios. Simply create your profile, get your unique link (perai.me/username), and paste it in your Instagram bio. When visitors click the link, they'll be able to talk to your personal AI twin directly. It works in Instagram's in-app browser, so the experience is seamless. Your AI twin can answer questions about your work, availability, rates, or anything you've added to your knowledge base - all without you having to respond to every DM."
          }
        },
        {
          "@type": "Question",
          "name": "Can I customize my AI's personality and responses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! You can customize your AI's tone, formatting style, emoji usage, and add a knowledge base with memories, Q&A pairs, and specific information. The AI learns from your profile data, knowledge base, and personality settings to respond in your unique style. Full customization available for all users."
          }
        },
        {
          "@type": "Question",
          "name": "What makes PerAI different from Linktree or other link-in-bio tools?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Unlike static link-in-bio pages, PerAI creates a personal AI twin that thinks and responds like you — 24/7. Instead of just displaying links, your profile becomes a live conversational experience where visitors ask questions about your work, availability, rates, and creative process. PerAI.me is the only link-in-bio tool where your profile actively thinks, talks, and converts visitors without you being there."
          }
        },
        {
          "@type": "Question",
          "name": "Interactive Link in Bio vs Static Bio - What's the difference?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "An Interactive Link in Bio (Smart Bio) is an AI-powered digital identity layer that maintains a 24/7 conversational presence, answering questions automatically and engaging visitors. A Static Bio is a traditional link-in-bio page that only displays links without interaction. PerAI.me transforms static bios into interactive Smart Bios with personalized AI identity and conversational marketing layers."
          }
        },
        {
          "@type": "Question",
          "name": "How do AI digital twins work for creators?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "AI digital twins for creators are high-fidelity AI clones that learn from your knowledge base, personality, and expertise to represent you online. PerAI.me creates digital persona hosting that enables your AI twin to respond to fan FAQs 24/7 automatically, automate Instagram DMs, and scale your personal brand without manual labor. Your digital twin maintains your voice and style while handling conversations around the clock."
          }
        },
        {
          "@type": "Question",
          "name": "How to automate Instagram DMs with AI?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "PerAI.me enables you to automate Instagram DMs with AI by creating a Smart Bio link in your Instagram bio. When visitors click your link, they interact with your AI assistant instead of sending DMs. Your AI answers questions about your work, availability, rates, and more - automatically responding to fan FAQs 24/7. This stops losing leads from link-in-bio and provides 24/7 lead capture for social media."
          }
        },
        {
          "@type": "Question",
          "name": "What is a Personal Intelligence Assistant?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A Personal Intelligence Assistant is a zero-latency knowledge base powered by AI that represents you online. PerAI.me creates personalized AI identity assistants that learn from your expertise, respond in your voice, and maintain a smart social identity. These assistants enable conversational marketing layers that scale personal brand without manual labor, providing 24/7 automated responses to inquiries."
          }
        },
        {
          "@type": "Question",
          "name": "How can Real Estate agents use AI for lead qualifying?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Real Estate agents use PerAI.me as an AI assistant for Real Estate lead qualifying. Your Smart Bio answers property questions instantly, qualifies buyers automatically, and captures contact information 24/7. This stops losing leads from link-in-bio and provides automated lead capture even when you're offline. Your AI assistant handles initial inquiries, schedules viewings, and scales your real estate business without manual labor."
          }
        },
        {
          "@type": "Question",
          "name": "Can influencers automate brand deal vetting?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, PerAI.me enables automated brand deal vetting for influencers. Your AI assistant can be trained on your rates, requirements, and brand guidelines. When brands inquire through your Smart Bio, your AI vets them automatically, answers questions about your media kit, and qualifies partnerships. This scales personal brand without manual labor and ensures you never miss a brand opportunity, even at 3 AM."
          }
        }
      ]
    }), []);

  // Memoize SoftwareApplication Schema (static data)
  const softwareSchema = useMemo(() => {
    const url = `${window.location.origin}/`;
    return {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PerAI.me",
      "alternateName": "PerAI",
      "applicationCategory": "MultimediaService",
      "applicationSubCategory": "Link-in-Bio Tool",
      "operatingSystem": "Web",
      "url": url,
      "description": "PerAI.me transforms your link-in-bio into a personal AI twin that thinks and talks like you. Train it once — it answers every question, captures leads, and engages visitors 24/7 while you focus on creating.",
      "keywords": "What is a Smart Bio, Interactive Link in Bio vs Static Bio, AI digital twins for creators, AI twin for Real Estate lead qualifying, Digital Teaching Twin for online coaches, Executive AI persona for networking, Automated brand deal vetting for influencers, Conversational portfolio for designers, How to automate Instagram DMs with AI, Respond to fan FAQs 24/7 automatically, Stop losing leads from link-in-bio, Scale personal brand without manual labor, 24/7 lead capture for social media, Personalized AI Identity, Conversational Marketing Layer, Digital Persona Hosting, High-fidelity AI twin, Zero-latency knowledge base, Smart Social Identity, Personal Intelligence Assistant, AI-powered link-in-bio, personal AI twin, linktree alternative",
      "provider": {
        "@type": "Organization",
        "name": "PerAI.me",
        "url": url,
        "logo": `${url}/favicon.svg`
      },
      "featureList": [
        "Personal AI Twin",
        "Custom AI Assistant",
        "Link Management",
        "Knowledge Base",
        "24/7 Automated Responses",
        "Instagram Bio Integration",
        "Social Media Links",
        "Profile Customization",
        "Conversation History",
        "Multi-Platform Support"
      ],
      "screenshot": `${url}/blog-header.png`,
      "softwareVersion": "3.0",
      "releaseNotes": "Enhanced AI capabilities with improved response quality and faster response times",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      },
      "inLanguage": "en"
    };
  }, []);

  // SEO: Update document title and meta tags
  useEffect(() => {
    const title = 'PerAI.me | Train Your AI Twin. Share One Link. Scale Yourself.';
    document.title = title;

    // Helper function to update or create meta tags
    const updateMetaTag = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || 
                 document.querySelector(`meta[name="${property}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('article:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    const description = 'PerAI.me transforms static Instagram bios and Linktree pages into interactive AI-powered profiles. Create your digital twin that answers questions 24/7. Free setup in 2 minutes.';
    const url = `${window.location.origin}/`;
    const image = `${window.location.origin}/blog-header.png`;
    
    // Open Graph tags
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', url);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:image', image);
    updateMetaTag('og:site_name', 'PerAI.me');
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    
    // Standard meta tags
    updateMetaTag('description', description);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${window.location.origin}/`);

    // Remove existing FAQ schema if present
    const existingFaqSchema = document.querySelector('script[type="application/ld+json"][data-faq-schema]');
    if (existingFaqSchema) {
      existingFaqSchema.remove();
    }
    
    // Add FAQ schema script
    const faqSchemaScript = document.createElement('script');
    faqSchemaScript.type = 'application/ld+json';
    faqSchemaScript.setAttribute('data-faq-schema', 'true');
    faqSchemaScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqSchemaScript);
    
    // Add SoftwareApplication Schema (AI-Friendly)
    const existingSoftwareSchema = document.querySelector('script[type="application/ld+json"][data-software-schema]');
    if (existingSoftwareSchema) {
      existingSoftwareSchema.remove();
    }
    
    const softwareSchemaScript = document.createElement('script');
    softwareSchemaScript.type = 'application/ld+json';
    softwareSchemaScript.setAttribute('data-software-schema', 'true');
    softwareSchemaScript.textContent = JSON.stringify(softwareSchema);
    document.head.appendChild(softwareSchemaScript);
    
    // Cleanup: Reset to default when component unmounts
    return () => {
      document.title = 'PerAI.me | The Bio That Speaks';
      // Remove FAQ schema on unmount
      const faqSchemaToRemove = document.querySelector('script[type="application/ld+json"][data-faq-schema]');
      if (faqSchemaToRemove) {
        faqSchemaToRemove.remove();
      }
      // Remove SoftwareApplication schema on unmount
      const softwareSchemaToRemove = document.querySelector('script[type="application/ld+json"][data-software-schema]');
      if (softwareSchemaToRemove) {
        softwareSchemaToRemove.remove();
      }
    };
  }, [faqSchema, softwareSchema]);


  // Step-by-step auto-play animation
  useEffect(() => {
    if (!isAutoPlaying) return;

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev >= 2) {
          // Reset to step 0 (no setTimeout needed - already resetting)
          return 0;
        }
        return prev + 1;
      });
    }, 3000); // Change step every 3 seconds

    return () => clearInterval(stepInterval);
  }, [isAutoPlaying]);

  // Reset typing when profile changes
  useEffect(() => {
    if (typingContainerRef.current) {
      typingContainerRef.current.textContent = '';
    }
  }, [currentProfile]);

  // Typing Effect for Chat Preview
  useEffect(() => {
    if (currentStep < 2) return; // Only show typing on step 3
    if (isLoadingProfiles) return; // Wait for profiles to load
    if (!demoProfiles[currentProfile]) return; // Wait for current profile to be loaded
    
    const typingContainer = typingContainerRef.current;
    if (!typingContainer) return;

    typingContainer.textContent = '';
    
    // Get response text based on current profile
    const profile = demoProfiles[currentProfile];
    let responseText = "";
    
    // Only showing Sarah's profile for now
    if (currentProfile === 'sarah') {
      // Camera/photo focused response
      if (profile?.knowledge && profile.knowledge.length > 0) {
        responseText = profile.knowledge.find(k => k.toLowerCase().includes('camera') || k.toLowerCase().includes('sony')) 
          || profile.knowledge[0] 
          || "I shoot with a Sony A7SIII and love the low-light performance. For lenses, I use a 24-70mm f/2.8 for most work and an 85mm f/1.4 for portraits.";
      } else {
        responseText = "I shoot with a Sony A7SIII and love the low-light performance. For lenses, I use a 24-70mm f/2.8 for most work and an 85mm f/1.4 for portraits.";
      }
    }
    
    let i = 0;

    const typeHero = () => {
      if (i < responseText.length) {
        typingContainer.textContent += responseText.charAt(i);
        i++;
        setTimeout(typeHero, 30);
      }
    };

    const timeoutId = setTimeout(typeHero, 500);
    return () => clearTimeout(timeoutId);
  }, [currentStep, currentProfile, demoProfiles, isLoadingProfiles]);

  // Preload profile avatar for faster rendering
  useEffect(() => {
    const profile = demoProfiles[currentProfile];
    if (profile?.avatar && profile.avatar.trim() && 
        !profile.avatar.startsWith('data:') &&
        (profile.avatar.startsWith('http://') || profile.avatar.startsWith('https://'))) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = profile.avatar;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      return () => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      };
    }
  }, [demoProfiles, currentProfile]);

  // Cycling text animation removed — static H1 is better for SEO and user clarity

  // Fetch real profiles for demo - Deferred to improve initial load time
  useEffect(() => {
    // Helper function to add timeout to Firestore queries
    const withTimeout = (promise, timeoutMs = 10000) => {
      return Promise.race([
        promise,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Query timeout')), timeoutMs)
        )
      ]);
    };

    // Helper function to retry Firestore queries
    const retryQuery = async (queryFn, maxRetries = 2, delay = 1000) => {
      for (let i = 0; i <= maxRetries; i++) {
        try {
          return await withTimeout(queryFn(), 10000);
        } catch (error) {
          // Suppress timeout errors from background Firestore channels (not critical)
          if (error.message.includes('timeout') || error.message.includes('TIMED_OUT')) {
            // Only log on last retry
            if (i === maxRetries) {
              console.warn('Firestore query timeout (non-critical):', error.message);
            }
          } else {
            // Re-throw non-timeout errors immediately
            throw error;
          }
          
          // Wait before retrying (exponential backoff)
          if (i < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
          }
        }
      }
      return null; // Return null if all retries failed
    };

    const fetchProfiles = async () => {
      try {
        // Fetch both profiles in parallel with timeout and retry logic
        const [sarahResult, nabinResult] = await Promise.all([
          retryQuery(() => getDocs(query(collection(landingDb, 'artifacts', landingAppId, 'users'), where('username', '==', 'sarah')))),
          retryQuery(() => getDocs(query(collection(landingDb, 'artifacts', landingAppId, 'users'), where('username', '==', 'nabin'))))
        ]);
        
        const profiles = {
          sarah: sarahResult && !sarahResult.empty ? sarahResult.docs[0].data() : null,
          nabin: nabinResult && !nabinResult.empty ? nabinResult.docs[0].data() : null
        };
        
        setDemoProfiles(profiles);
        setIsLoadingProfiles(false);
      } catch (error) {
        // Only log unexpected errors (not timeout errors from background channels)
        if (!error.message?.includes('timeout') && !error.message?.includes('TIMED_OUT')) {
          console.error('Error fetching demo profiles:', error);
        }
        setIsLoadingProfiles(false);
      }
    };
    
    // Defer profile fetching to improve initial page load
    // Use requestIdleCallback if available, otherwise small delay
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fetchProfiles, { timeout: 2000 });
    } else {
      setTimeout(fetchProfiles, 100);
    }
  }, []);

  // Profile rotation disabled - only showing Sarah's profile for now

  // Add class to body for cursor hiding
  useEffect(() => {
    document.body.classList.add('landing-page');
    return () => {
      document.body.classList.remove('landing-page');
    };
  }, []);

  // 3D Phone Tilt Effect with Container-Relative Mouse Tracking (Throttled for performance)
  useEffect(() => {
    let ticking = false;
    
    const handleMouseMove = (e) => {
      if (!heroContainerRef.current || !phoneRef.current || ticking) return;
      
      ticking = true;
      requestAnimationFrame(() => {
        const containerRect = heroContainerRef.current.getBoundingClientRect();
        const x = (e.clientX - containerRect.left) / containerRect.width - 0.5;
        const y = (e.clientY - containerRect.top) / containerRect.height - 0.5;
        
        setMousePosition({ x, y });
        
        // Calculate rotation for phone (max 15 degrees)
        const rotateX = y * -10;
        const rotateY = x * 15;
        
        if (phoneRef.current) {
          phoneRef.current.style.transform = `rotateY(${rotateY}deg) rotateX(${rotateX}deg)`;
          phoneRef.current.style.boxShadow = `${x * -20}px ${y * 20}px 60px rgba(255,255,255,0.02)`;
        }
        
        ticking = false;
      });
    };

    if (heroContainerRef.current) {
      heroContainerRef.current.addEventListener('mousemove', handleMouseMove);
      return () => {
        if (heroContainerRef.current) {
          heroContainerRef.current.removeEventListener('mousemove', handleMouseMove);
        }
      };
    }
  }, []);

  // Training progress animation
  useEffect(() => {
    const interval = setInterval(() => {
      setTrainingProgress(prev => (prev < 100 ? prev + Math.random() * 0.1 : 78));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile menu when clicking outside or pressing ESC
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        // Check if click is not on the menu button
        const menuButton = event.target.closest('button[aria-label="Toggle mobile menu"]');
        if (!menuButton) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white font-['Plus Jakarta Sans',sans-serif] overflow-x-hidden relative" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Noise Overlay */}
      <div className="noise-overlay" />


      {/* Ambient Background - Mutiny-Lime */}
      <div className="ambient-glow fixed w-[900px] h-[900px] bg-gradient-radial from-white/3 to-transparent top-[30%] left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10" />

      {/* Navigation - Updated Design */}
      <nav className="fixed top-0 left-0 right-0 z-[60] bg-black/80 border-b-2 border-white/10 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-20 flex items-center justify-between uppercase font-black italic tracking-tighter" style={{ paddingTop: 'max(0.85rem, env(safe-area-inset-top))' }}>
          {/* Brand Section - Original Animated Logo */}
          <div className="flex items-center gap-7 group cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <div className="text-xl font-bold font-['Syne',sans-serif] tracking-wide">
              <span className="ai-shimmer text-xl font-bold relative inline-block" data-text="PerAI.me">
            <span className="ai-shimmer-particles">
                  {/* Sparkling particles - UNCHANGED */}
              {[...Array(12)].map((_, i) => {
                const angle = (i * 30) * (Math.PI / 180);
                const radius = 60 + (i % 3) * 15;
                const tx = Math.cos(angle) * radius;
                const ty = Math.sin(angle) * radius;
                return (
                  <span
                    key={i}
                    className="ai-shimmer-particle"
                    style={{
                      '--tx': `${tx}px`,
                      '--ty': `${ty}px`,
                      left: '50%',
                      top: '50%',
                      animationDelay: `${i * 0.2}s`,
                      animationDuration: `${2 + (i % 3) * 0.5}s`
                    }}
                  />
                );
              })}
            </span>
                {/* Swirling trails - UNCHANGED */}
            {[...Array(3)].map((_, i) => (
              <span
                key={i}
                className="ai-shimmer-trail"
                style={{
                  animationDelay: `${i * 1.3}s`,
                  animationDuration: `${4 + i * 0.5}s`
                }}
              />
            ))}
            PerAI.me
          </span>
        </div>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-10 text-xs tracking-widest font-['Plus Jakarta Sans',sans-serif]">
            <button
              onClick={() => navigate('/solutions')}
              className="min-h-[44px] py-3 hover:text-white transition-colors"
            >
              Solutions
            </button>
            <button
              onClick={() => navigate('/templates')}
              className="min-h-[44px] py-3 hover:text-white transition-colors"
            >
              Templates
            </button>
            <button
              onClick={() => navigate('/blog')}
              className="min-h-[44px] py-3 hover:text-white transition-colors"
            >
              Blog
            </button>
            <button
              onClick={() => navigate('/comparison')}
              className="min-h-[44px] py-3 hover:text-white transition-colors"
            >
              Comparison
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/auth')}
              className="hidden md:block px-6 py-[11px] min-h-[44px] border border-white/30 text-white text-xs font-semibold hover:border-white/50 hover:bg-white/10 transition-all uppercase"
          >
            Login
          </button>
          {/* Create Persona is desktop-only in the nav. On mobile the
              CTA lives inside the menu drawer so the nav stays clean
              (logo + hamburger only). The hero still has the primary
              CTA above the fold on mobile. */}
          <button
            onClick={onGetStarted}
              className="hidden md:block group relative px-8 py-3 bg-[#C1FF00] text-black text-sm font-bold hover:bg-[#B8FF33] transition-all shadow-lg hover:shadow-xl uppercase transform hover:scale-105 active:scale-100"
          >
            <span className="relative z-10">Create Persona</span>
            <div className="absolute inset-0 bg-white/20 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>

            {/* Mobile Menu Button */}
              <button
              className="md:hidden p-2 text-gray-300 hover:text-white transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
              >
              <span className="text-sm font-semibold uppercase">MENU</span>
          </button>
            </div>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Menu */}
          <div 
            ref={mobileMenuRef}
            className="fixed top-[80px] left-0 right-0 z-40 bg-black/95 backdrop-blur-md border-b-2 border-white/10 md:hidden"
          >
            <div className="px-6 py-4 space-y-4 max-w-[1400px] mx-auto">
              <button 
                onClick={() => {
                  navigate('/solutions');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left text-xs text-gray-300 hover:text-white transition-colors py-2 uppercase tracking-widest font-['Plus Jakarta Sans',sans-serif] font-semibold"
              >
                Solutions
              </button>
              <button 
                onClick={() => {
                  navigate('/templates');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left text-xs text-gray-300 hover:text-white transition-colors py-2 uppercase tracking-widest font-['Plus Jakarta Sans',sans-serif] font-semibold"
              >
                Templates
              </button>
              <button 
                onClick={() => {
                  navigate('/blog');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left text-xs text-gray-300 hover:text-white transition-colors py-2 uppercase tracking-widest font-['Plus Jakarta Sans',sans-serif] font-semibold"
              >
                Blog
              </button>
              <button 
                onClick={() => {
                  navigate('/comparison');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left text-xs text-gray-300 hover:text-white transition-colors py-2 uppercase tracking-widest font-['Plus Jakarta Sans',sans-serif] font-semibold"
              >
                Comparison
              </button>
              <button 
                onClick={() => {
                  navigate('/auth');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left text-xs text-gray-300 hover:text-white transition-colors py-2 border-t border-white/10 pt-4 mt-2 uppercase tracking-widest font-['Plus Jakarta Sans',sans-serif] font-semibold"
              >
                Login
              </button>
              <button 
                onClick={() => {
                  onGetStarted();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-center bg-[#C1FF00] text-black font-bold text-sm py-4 rounded transition-all shadow-lg hover:shadow-xl uppercase tracking-widest font-['Plus Jakarta Sans',sans-serif] transform active:scale-95"
              >
                Create Persona
              </button>
        </div>
                  </div>
        </>
      )}

      <main>
      {/* Hero Section */}
      <section 
        ref={heroContainerRef}
        className="relative pt-20 sm:pt-28 pb-20 sm:pb-28 px-4 sm:px-6 overflow-hidden border-b-2 border-white/5 bg-[#0F0F0F] min-h-[100dvh] flex flex-col lg:flex-row items-center justify-center"
        id="hero"
      >
        {/* Dynamic Grid Background */}
        <div 
          className="absolute inset-0 opacity-[0.05] pointer-events-none transition-transform duration-1000 ease-out"
          style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 0)',
            backgroundSize: '40px 40px',
            transform: `translate(${mousePosition.x * -20}px, ${mousePosition.y * -20}px)`
          }}
        ></div>

        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-10 items-center relative z-10 w-full">
          {/* Left Section - Text Content */}
          <div className="text-left animate-in slide-in-from-left duration-700 fade-in-up">
            {/* Clear Value Proposition */}
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-black text-white mb-4 sm:mb-5 leading-[1.1]">
              <span style={{ color: '#C1FF00' }}>Your AI Twin.</span>{' '}One Link. Infinite Reach.
            </h1>
            
            {/* Simple Subheadline */}
            <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed">
              Train it once on your voice and expertise. It answers every question, qualifies leads, and engages your audience — 24/7, while you focus on creating.
            </p>

            {/* Social proof / trust line */}
            <p className="text-gray-500 text-xs uppercase tracking-[0.25em] font-semibold mb-8 sm:mb-11">
              <span className="w-1.5 h-1.5 bg-[#C1FF00] rounded-full inline-block mr-2 align-middle animate-pulse"></span>
              The first personal AI that lives in your bio
            </p>

            {/* Buttons — primary stays prominent, "Just Explore" demoted
                to a small text link so the eye lands on the real CTA. */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-start gap-4 sm:gap-6">
              <button
                onClick={onGetStarted}
                className="group relative w-full sm:w-auto px-8 sm:px-[60px] py-6 sm:py-8 bg-[#C1FF00] text-black font-bold uppercase text-sm sm:text-base overflow-hidden transition-all shadow-lg hover:shadow-xl active:scale-95"
              >
                <span className="relative z-10">Claim Your /Handle</span>
                <div className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
              </button>
              <button
                onClick={() => navigate('/solutions')}
                className="min-h-[44px] px-2 flex items-center text-white/70 hover:text-white font-semibold uppercase text-xs tracking-widest underline-offset-4 hover:underline transition-colors"
              >
                or just explore →
              </button>
            </div>
            </div>

          {/* Right Section - 3D Phone Mockup */}
          <div className="relative flex justify-center perspective-1000 fade-in-up-delay-2">
            {/* Phone Frame */}
            <div 
              ref={phoneRef}
              className="w-full max-w-[280px] sm:max-w-[303px] h-auto aspect-[303/607] sm:h-[607px] bg-[#050505] border-[7px] sm:border-[9px] border-white/20 rounded-[36px] sm:rounded-[48px] relative overflow-hidden shadow-[0_0_90px_rgba(255,255,255,0.05)] transition-transform duration-200 ease-out mx-auto"
              style={{ 
                transform: `rotateY(${mousePosition.x * 15}deg) rotateX(${mousePosition.y * -10}deg)`,
                boxShadow: `${mousePosition.x * -20}px ${mousePosition.y * 20}px 60px rgba(255,255,255,0.02)`
              }}
            >
              {/* Phone Screen Inner */}
              <div className="h-full w-full overflow-y-auto scrollbar-hide p-4 sm:p-7 flex flex-col items-center">
                {/* Dynamic Status Bar — uses LIVE indicator instead of the
                    classic 9:41 AM stock-mockup tell. Reads as a real
                    session, not a marketing screenshot. */}
                <div className="w-full flex justify-between items-center mb-8 px-2 opacity-60">
                  <span className="text-[8px] font-semibold uppercase font-['Courier New',monospace] text-white/70">Profile Active</span>
                  <span className="flex items-center gap-1 text-[8px] font-semibold uppercase font-['Courier New',monospace] text-[#C1FF00]">
                    <span className="w-1.5 h-1.5 bg-[#C1FF00] rounded-full animate-pulse"></span>
                    Live
                  </span>
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 bg-white/60 rounded-full animate-pulse"></div>
                    <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
                </div>
                  </div>

                {/* Profile Section */}
                  {(() => {
                    const profile = demoProfiles[currentProfile] || null;
                  const profileName = profile?.name || (currentProfile === 'sarah' ? 'Sarah Jenkins' : 'Zane Sterling');
                  const profileTagline = profile?.tagline || profile?.profession || (currentProfile === 'sarah' ? 'Designer & Visual Storyteller' : 'Founding Engineer & Creative Professional');
                  const rawAvatar = profile?.avatar;
                  const profileAvatar = (rawAvatar && !rawAvatar.startsWith('data:')) ? rawAvatar : 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?fit=crop&w=250&h=250';
                  // Optimize image URL for display size (112px = w-28 h-28)
                  // Generate responsive srcset: 1x (112px), 2x (224px), 3x (336px) for different screen densities
                  const baseWidth = 112;
                  const baseHeight = 112;
                  const optimizedAvatar = optimizeImageUrl(profileAvatar, baseWidth * 2, baseHeight * 2); // 2x for retina as fallback
                  const avatarSrcSet = generateSrcSet(profileAvatar, baseWidth, baseHeight, [1, 2, 3]);
                    
                    return (
                      <>
                      {/* Profile Picture */}
                      <div className="relative mb-7 group cursor-pointer">
                        <div className="absolute inset-0 bg-white/10 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
                        <div className="w-28 h-28 bg-neutral-900 rounded-full border-2 border-white/20 overflow-hidden relative z-10 ring-3 ring-black">
                          <img 
                                src={optimizedAvatar}
                                srcSet={avatarSrcSet || undefined}
                                sizes="112px"
                                alt={profileName}
                                width={112}
                                height={112}
                                loading="eager"
                                decoding="async"
                                fetchPriority="high"
                            className="w-full h-full object-cover grayscale brightness-110 hover:grayscale-0 transition-all duration-700" 
                              />
              </div>
            </div>

                      {/* Name - Italic */}
                      <h2 className="text-2xl font-['Plus Jakarta Sans',sans-serif] font-bold text-white tracking-tight mb-1 italic">{profileName}</h2>
                      <p className="text-white/70 text-[8px] font-semibold uppercase tracking-[0.3em] mb-8 border-b border-white/10 pb-1.5 font-['Courier New',monospace]">
                        {profileTagline}
                      </p>

                      {/* Interactive AI Chat Button */}
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Use setTimeout to defer state update and avoid React error #306
                          setTimeout(() => {
                            setShowChatModal(true);
                          }, 0);
                        }}
                        className="w-full py-4 bg-[#0a0a0a] border-2 border-white/20 rounded-xl flex items-center justify-between px-5 text-white font-semibold uppercase text-[9px] group hover:bg-white/10 hover:border-white/40 transition-all mb-5 relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-white/5 -translate-x-full group-hover:translate-x-0 transition-transform"></div>
                        <div className="relative z-10 text-left">
                          <span className="block text-[6px] text-white/60 group-hover:text-white/80 tracking-widest mb-0.5 uppercase">AI Online</span>
                          Ask my AI anything
                </div>
                        <div className="relative z-10 w-7 h-7 bg-white/10 group-hover:bg-white/20 rounded-full flex items-center justify-center">
                          <ArrowRight className="w-3.5 h-3.5 text-white/70 group-hover:text-white" />
                  </div>
                      </button>

                      {/* Core Links */}
                      <div className="w-full space-y-2.5 mb-10">
                        <div className="w-full py-3.5 bg-[#111] border border-white/10 rounded-xl flex items-center gap-3.5 px-5 text-white font-semibold uppercase text-[8px] hover:border-white/30 transition-all cursor-pointer font-['Courier New',monospace]">
                          <span className="text-white/60">01</span>
                          Portfolio Archive
                </div>
                        <div className="w-full py-3.5 bg-[#111] border border-white/10 rounded-xl flex items-center gap-3.5 px-5 text-white font-semibold uppercase text-[8px] hover:border-white/30 transition-all cursor-pointer font-['Courier New',monospace]">
                          <span className="text-white/60">02</span>
                          My Links
              </div>
            </div>

                      {/* Socials */}
                      <div className="flex gap-3.5 mb-8">
                        {[
                          <Instagram key="ig" className="w-4 h-4" />,
                          <Twitter key="tw" className="w-4 h-4" />,
                          <Linkedin key="li" className="w-4 h-4" />
                        ].map((icon, idx) => (
                          <div key={idx} className="w-10 h-10 bg-[#111] border border-white/10 rounded-xl flex items-center justify-center text-white hover:text-white hover:border-white/30 hover:scale-110 transition-all cursor-pointer">
                            {icon}
                </div>
                        ))}
                  </div>

                      {/* Floating Logs - Recent Memories */}
                      <div className="w-full text-left bg-black/40 p-3.5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-2.5">
                          <span className="text-[7px] font-semibold text-gray-400 uppercase tracking-widest font-['Courier New',monospace]">Recent Posts</span>
                          <div className="w-1.5 h-1.5 bg-white/60 rounded-full animate-pulse"></div>
                </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="aspect-square bg-neutral-900 rounded-lg overflow-hidden grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-500">
                            <img 
                              src={optimizeImageUrl("https://images.unsplash.com/photo-1518770660439-4636190af475?fit=crop&w=150&h=150", 300, 300)}
                              srcSet={generateSrcSet("https://images.unsplash.com/photo-1518770660439-4636190af475?fit=crop&w=150&h=150", 150, 150, [1, 2]) || undefined}
                              sizes="150px"
                              alt="Recent memory or post from PerAI profile showcase" 
                              width={150}
                              height={150}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover" 
                              aria-label="Profile memory image" 
                            />
              </div>
                          <div className="aspect-square bg-neutral-900 rounded-lg overflow-hidden grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-500">
                            <img 
                              src={optimizeImageUrl("https://images.unsplash.com/photo-1531297484001-80022131f5a1?fit=crop&w=150&h=150", 300, 300)}
                              srcSet={generateSrcSet("https://images.unsplash.com/photo-1531297484001-80022131f5a1?fit=crop&w=150&h=150", 150, 150, [1, 2]) || undefined}
                              sizes="150px"
                              alt="Recent memory or post from PerAI profile showcase" 
                              width={150}
                              height={150}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover" 
                              aria-label="Profile memory image" 
                            />
                              </div>
                              </div>
                            </div>
                      </>
                    );
                  })()}

                  {/* Floating Chat Widget Overlay Inside Phone */}
                  <div className="absolute bottom-8 right-6 z-30 animate-float-widget">
                    <div className="relative">
                      {/* Chat Bubble */}
                      <div 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setTimeout(() => {
                            setShowChatModal(true);
                          }, 0);
                        }}
                        className="absolute bottom-16 right-0 bg-white rounded-[24px] rounded-br-none px-6 py-3.5 shadow-[0_10px_30px_rgba(255,255,255,0.1)] animate-bounce-slow whitespace-nowrap cursor-pointer hover:scale-105 transition-transform"
                      >
                        <span className="text-black font-black text-[11px] tracking-tight">Say Hi:</span>
                        {/* Tiny Triangle Pointer */}
                        <div className="absolute -bottom-2 right-0 w-3 h-3 bg-white clip-path-bubble-tail"></div>
                      </div>

                      {/* Avatar Widget */}
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setTimeout(() => {
                            setShowChatModal(true);
                          }, 0);
                        }}
                        className="group relative w-16 h-16 bg-[#050505] rounded-full border-2 border-white/20 p-1 hover:border-[#C1FF00] transition-all duration-500 hover:scale-110 active:scale-95"
                      >
                        <div className="w-full h-full rounded-full overflow-hidden border-2 border-black">
                          <img
                            src={(() => { const av = demoProfiles[currentProfile]?.avatar; return (av && !av.startsWith('data:')) ? av : 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?fit=crop&w=150&h=150'; })()}
                            alt="Chat Mini Avatar"
                            className="w-full h-full object-cover grayscale"
                          />
                        </div>
                        {/* Online indicator */}
                        <div className="absolute top-0 right-0 w-5 h-5 bg-[#22c55e] border-[3.5px] border-white rounded-full flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-black/30 rounded-full animate-pulse"></div>
                        </div>
                      </button>
                    </div>
                  </div>
            </div>

              {/* Reflection Overlay */}
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-white/5 to-transparent opacity-20"></div>
              </div>

            {/* Label Sticker */}
              <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Use setTimeout to defer state update and avoid React error #306
                setTimeout(() => {
                  setShowChatModal(true);
                }, 0);
              }}
              className="absolute -right-10 top-1/4 -rotate-90 bg-white text-black px-7 py-2.5 text-[9px] font-semibold uppercase tracking-[0.4em] hover:bg-gray-100 hover:translate-y-[-5px] transition-all cursor-pointer border-2 border-black shadow-lg hidden lg:block"
              >
              Try Live Demo
              </button>
            </div>
          </div>
      </section>

      {/* Solutions and Templates sections moved to separate pages: /solutions and /templates */}

      {/* Total Ecosystem Section */}
      <section className="w-full py-20 sm:py-28 bg-[#0F0F0F] border-y-2 border-white/10 fade-in-up" id="features">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="mb-16 text-center">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-['Plus Jakarta Sans',sans-serif] font-bold uppercase italic tracking-tighter mb-3" aria-label="Total Ecosystem - PerAI.me platform features">
              Total <span className="text-white">Ecosystem</span>
          </h2>
            <p className="text-gray-300 font-bold uppercase tracking-[0.3em] text-xs font-['Courier New',monospace]">
              8 Modules // 1 Unified Intelligence
          </p>
                            </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
            {[
              {
                id: 'e1',
                code: 'IDENTITY MODULE',
                title: 'Identity',
                description: 'Your core presence. Manage your name, bio, avatar, and unique perai.me handle.',
                icon: User,
                number: '01'
              },
              {
                id: 'e2',
                code: 'AI BRAIN MODULE',
                title: 'AI Brain',
                description: 'Teach your AI assistant. Add your knowledge and answers so it responds just like you.',
                icon: Lightbulb,
                number: '02'
              },
              {
                id: 'e3',
                code: 'BUSINESS MODULE',
                title: 'Business',
                description: 'Monetize your bio with spotlights, product listings, and direct booking integrations.',
                icon: Briefcase,
                number: '03'
              },
              {
                id: 'e4',
                code: 'STACK MODULE',
                title: 'My Stack',
                description: 'The ultimate aggregator. Link every social profile, store, and website in one hub.',
                icon: Layers,
                number: '04'
              },
              {
                id: 'e5',
                code: 'MEMORIES MODULE',
                title: 'Memories',
                description: 'Media persistence. Upload images and video reels directly to your bio timeline.',
                icon: Image,
                number: '05'
              },
              {
                id: 'e6',
                code: 'CHAT SETTINGS MODULE',
                title: 'Chat Settings',
                description: 'Script the interaction. Customize intro text and quick-prompt suggestion buttons.',
                icon: Settings,
                number: '06'
              },
              {
                id: 'e7',
                code: 'CHATS MODULE',
                title: 'Chats',
                description: 'Full interaction history. View complete logs of every conversation your AI had.',
                icon: MessageCircle,
                number: '07'
              },
              {
                id: 'e8',
                code: 'PROFILE CORE MODULE',
                title: 'Profile Core',
                description: 'Total aesthetic control. Define button styles, themes, and platform-wide artifacts.',
                icon: Settings,
                number: '08'
              }
            ].map((module) => {
              const IconComponent = module.icon;
              return (
                <div 
                  key={module.id}
                  onMouseEnter={() => setActiveEcosystemCard(module.id)}
                  onMouseLeave={() => setActiveEcosystemCard(null)}
                  className={`group relative p-5 sm:p-6 transition-all duration-500 cursor-pointer ${
                    activeEcosystemCard === module.id ? 'bg-white' : 'bg-black'
                  }`}
                  aria-label={`Module: ${module.title} - ${module.description}`}
                >
                  <div className="absolute top-0 right-0 p-2 sm:p-3 opacity-20 group-hover:opacity-100 transition-opacity">
                    <span className={`text-[9px] font-black px-2 py-1 font-['Courier New',monospace] ${
                      activeEcosystemCard === module.id ? 'bg-black text-white' : 'bg-[#C1FF00] text-black'
                    }`}>
                      ACTIVE
                    </span>
                  </div>

                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 sm:w-9 sm:h-9 border-2 flex items-center justify-center font-black transition-all duration-500 text-sm font-['Courier New',monospace] ${
                        activeEcosystemCard === module.id ? 'border-black text-black' : 'border-white/10 text-white/30'
                      }`}>
                        {module.number}
                      </div>
                      <div className={`text-[9px] font-['Courier New',monospace] tracking-widest transition-colors ${
                        activeEcosystemCard === module.id ? 'text-black/40' : 'text-gray-400'
                      }`}>
                        // {module.code}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <h3 className={`text-xl sm:text-2xl font-['Plus Jakarta Sans',sans-serif] font-black uppercase italic tracking-tighter transition-all duration-500 ${
                        activeEcosystemCard === module.id ? 'text-black translate-x-2' : 'text-white'
                      }`}>
                        {module.title}
                      </h3>
                      <div className={`opacity-40 transition-opacity duration-500 ${
                        activeEcosystemCard === module.id ? 'opacity-100' : 'opacity-40'
                      }`}>
                        <IconComponent size={20} className={activeEcosystemCard === module.id ? 'text-black' : 'text-white'} />
                      </div>
                    </div>

                    <p className={`text-xs font-bold uppercase leading-relaxed tracking-tight transition-colors duration-500 font-['Courier New',monospace] ${
                      activeEcosystemCard === module.id ? 'text-black/70' : 'text-gray-300'
                    }`}>
                      {module.description}
                    </p>

                    <div className="pt-4 sm:pt-5 border-t border-current opacity-10"></div>

                    {activeEcosystemCard === module.id && (
                      <div className="flex flex-col items-end animate-in fade-in slide-in-from-right duration-300">
                        <span className="text-[8px] font-black text-[#C1FF00] uppercase tracking-widest mb-1 bg-black px-2 py-0.5 font-['Courier New',monospace]">Enabled</span>
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#C1FF00] text-black flex items-center justify-center rounded-full animate-bounce">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  {activeEcosystemCard === module.id && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-5">
                      <div className="w-full h-full bg-[linear-gradient(transparent_0%,rgba(0,0,0,0.2)_50%,transparent_100%)] bg-[length:100%_4px] animate-pulse"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Command Center Section */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#0F0F0F] border-t border-white/5 fade-in-up" id="command-center">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 sm:gap-14 items-start">
            {/* Left Sidebar */}
            <div className="flex-1 lg:sticky lg:top-32">
              <div className="inline-flex items-center gap-2 bg-white text-black px-2 py-1 font-black text-[7px] uppercase mb-6 tracking-widest font-['Courier New',monospace]">
                Step 2 // AI Training
              </div>
              <h2 className="text-5xl lg:text-7xl font-['Plus Jakarta Sans',sans-serif] font-black uppercase italic tracking-tighter mb-3 leading-[0.85]" aria-label="How to Train Your AI Assistant - Step by step guide">
                Train Your <br />
                <span className="text-[#C1FF00]">AI Assistant</span>
          </h2>
              <p className="text-gray-300 text-xs font-bold uppercase mb-2 leading-tight max-w-sm font-['Courier New',monospace]">
                How It Works
              </p>
              <p className="text-gray-300 text-sm font-bold uppercase mb-8 leading-tight max-w-sm font-['Courier New',monospace]">
                Static bios are dead. Feed your duplicate your expertise and let it scale your presence.
              </p>
              
              {/* How It Works Steps - Merged with Training Steps Style */}
              <div className="space-y-4 mb-10">
                {[
                  { n: "01", t: "Create Your Profile", desc: "Add your name, photo, bio, and links. Takes less than 2 minutes.", active: false },
                  { n: "02", t: "Train Your AI", desc: "Add your knowledge, preferences, and FAQs. Your AI learns to respond like you.", active: true },
                  { n: "03", t: "Share & Let It Work", desc: "Share your link anywhere. Your AI handles conversations while you focus on what matters.", active: false }
                ].map((step) => (
                  <div key={step.n} className="flex items-start gap-4 group cursor-default">
                    <div className={`w-10 h-10 border-2 flex items-center justify-center font-black transition-all font-['Courier New',monospace] flex-shrink-0 ${
                      step.active 
                        ? 'bg-[#C1FF00] border-black text-black scale-110 shadow-[4px_4px_0px_white]' 
                        : 'border-white/10 text-gray-300 group-hover:border-white group-hover:text-white'
                    }`}>
                      {step.n}
                </div>
                    <div className="flex-1">
                      <div className={`text-[9px] font-black uppercase tracking-[0.2em] transition-colors mb-1 font-['Courier New',monospace] ${
                        step.active ? 'text-white' : 'text-gray-300 group-hover:text-gray-200'
                      }`}>
                        {step.t}
              </div>
                      <p className={`text-[8px] font-['Courier New',monospace] uppercase tracking-tight leading-relaxed transition-colors ${
                        step.active ? 'text-gray-400' : 'text-gray-300 group-hover:text-gray-200'
                      }`}>
                        {step.desc}
              </p>
            </div>
              </div>
                ))}
            </div>

              <button 
                onClick={onGetStarted}
                className="group relative w-full lg:w-auto px-9 py-5 bg-white text-black font-black uppercase text-[10px] transition-all hover:bg-[#C1FF00] hover:-translate-y-1 shadow-[6px_6px_0px_rgba(193,255,0,0.2)] font-['Courier New',monospace]"
              >
                Start in 2 Minutes →
              </button>
          </div>

            {/* Right Terminal Interface */}
            <div className="flex-[1.8] w-full">
              <div className="bg-[#050505] border-[4px] border-white/10 shadow-[20px_20px_0px_#C1FF00] md:shadow-[28px_28px_0px_#C1FF00] overflow-hidden flex flex-col md:flex-row min-h-[400px] md:min-h-[550px] relative">
                
                {/* Terminal CRT Grain Effect */}
                <div className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-overlay" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                  backgroundSize: '200px 200px'
                }}></div>

                {/* Sidebar */}
                <div className="w-full md:w-16 border-b md:border-b-0 md:border-r border-white/10 flex md:flex-col bg-[#080808] z-10">
                  {[
                    { icon: <User size={14} />, active: false },
                    { icon: <Lightbulb size={14} />, active: true },
                    { icon: <Layers size={14} />, active: false },
                    { icon: <Briefcase size={14} />, active: false },
                    { icon: <Image size={14} />, active: false },
                    { icon: <MessageCircle size={14} />, active: false }
                  ].map((item, i) => (
                    <div 
                      key={i} 
                      className={`flex-1 md:flex-none p-4 flex items-center justify-center cursor-pointer transition-all ${
                        item.active 
                          ? 'bg-[#C1FF00] text-black' 
                          : 'text-gray-300 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <span>{item.icon}</span>
              </div>
                  ))}
                  <div className="mt-auto hidden md:flex p-4 items-center justify-center text-[#C1FF00]/30 animate-pulse">
                    <div className="w-1.5 h-1.5 bg-[#C1FF00] rounded-full"></div>
            </div>
              </div>

                {/* Main Stage */}
                <div className="flex-1 bg-black flex flex-col z-10">
                  {/* Dashboard Header */}
                  <div className="p-6 border-b border-white/5 bg-gradient-to-r from-white/5 to-transparent">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-6">
                      <div>
                        <h3 className="text-xl font-black uppercase italic tracking-tighter text-white font-['Plus Jakarta Sans',sans-serif]">
                          AI Training
                        </h3>
                        <p className="text-[8px] font-black text-[#C1FF00] uppercase tracking-[0.4em] mt-1 font-['Courier New',monospace]">
                          Active Profile: Zane Sterling
              </p>
            </div>
                      <div className="flex gap-4">
                        <div className="text-right">
                          <span className="block text-[6px] font-black text-gray-300 uppercase mb-1 font-['Courier New',monospace]">
                            Learning Progress
                          </span>
                          <span className="text-lg font-black text-white italic font-['Plus Jakarta Sans',sans-serif]">
                            {trainingProgress.toFixed(1)}%
                          </span>
          </div>
                        <div className="w-12 h-12 rounded-full border-2 border-white/5 flex items-center justify-center relative overflow-hidden">
                          <div 
                            className="absolute bottom-0 left-0 right-0 bg-[#C1FF00]/20 transition-all duration-500" 
                            style={{ height: `${trainingProgress}%` }}
                          ></div>
                          <span className="text-[#C1FF00] relative z-10">
                            <Lightbulb size={17} />
                          </span>
                </div>
              </div>
            </div>

                    {/* Status Bar */}
                    <div className="h-1 bg-white/5 w-full relative overflow-hidden">
                      <div 
                        className="absolute inset-y-0 left-0 bg-[#C1FF00] transition-all duration-500" 
                        style={{ width: `${trainingProgress}%` }}
                      ></div>
              </div>
            </div>

                  {/* Q&A List */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hide">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-[7px] font-black text-gray-300 uppercase tracking-[0.5em] font-['Courier New',monospace]">
                        // Questions & Answers
                      </span>
                      <button className="border border-[#C1FF00]/30 text-[#C1FF00] px-3 py-1 text-[7px] font-black uppercase hover:bg-[#C1FF00] hover:text-black transition-all font-['Courier New',monospace]">
                        + Add New
                      </button>
          </div>

                    {[
                      { 
                        q: "Current Project Focus", 
                        a: "Building the next generation of PerAI. Focus: fast responses and personalized AI assistants.", 
                        status: "Ready" 
                      },
                      { 
                        q: "Favorite Tech Stack", 
                        a: "Rust for heavy lifting, React for the interface, and AI for smart responses.", 
                        status: "Ready" 
                      },
                      { 
                        q: "Advice for Founders", 
                        a: "Build in public. Your community is your moat. An AI-powered bio is just the beginning.", 
                        status: "Learning" 
                      }
                    ].map((item, i) => (
                      <div 
                        key={i} 
                        className="group bg-[#0a0a0a] border border-white/5 p-6 hover:border-[#C1FF00]/50 transition-all cursor-pointer relative overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 p-2">
                          <span className={`text-[5px] font-black px-1 py-0.5 font-['Courier New',monospace] ${
                            item.status === 'SYNCED' 
                              ? 'bg-[#C1FF00]/10 text-[#C1FF00]' 
                              : 'bg-white/10 text-white animate-pulse'
                          }`}>
                            {item.status === 'SYNCED' ? 'Ready' : 'Learning'}
                          </span>
                </div>
                        <div className="flex gap-4">
                          <div className="text-white/20 font-black text-[9px] pt-1 font-['Courier New',monospace]">
                            Q_0{i+1}
              </div>
                          <div className="flex-1">
                            <h4 className="text-white font-black uppercase italic text-xs mb-2 tracking-tight group-hover:text-[#C1FF00] transition-colors font-['Plus Jakarta Sans',sans-serif]">
                              {item.q}
                            </h4>
                            <div className="text-[9px] font-mono text-gray-300 uppercase leading-relaxed tracking-tight font-['Courier New',monospace]">
                              {item.a}
                            </div>
              </div>
            </div>
          </div>
                    ))}
        </div>

                  {/* Training Footer */}
                  <div className="p-6 border-t border-white/5 bg-[#050505] flex justify-between items-center text-[7px] font-black text-gray-400 uppercase tracking-widest font-['Courier New',monospace]">
                    <div className="flex gap-4">
                      <span>AI Version: 2.1</span>
                      <span>Response Time: Instant</span>
                          </div>
                    <button className="text-[#C1FF00] hover:underline cursor-pointer">
                      Export Settings
          </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#080808] relative overflow-hidden border-t border-white/5 fade-in-up">
        {/* Noise Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }}></div>
        
        {/* Grid Background */}
        <div className="absolute inset-0 opacity-[0.1] pointer-events-none" style={{ 
          backgroundImage: 'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)', 
          backgroundSize: '100px 100px' 
        }}></div>

        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 lg:gap-16 mb-16 sm:mb-20">
            {/* Left Side - Header */}
            <div className="flex-shrink-0 lg:w-1/3 xl:w-2/5">
              <div className="inline-flex items-center gap-3 bg-red-600 text-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.3em] mb-8 animate-pulse">
                <span className="w-2 h-2 bg-white rounded-full"></span>
                RESULTS: PROFILE IMPROVEMENTS NEEDED
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-['Plus Jakarta Sans',sans-serif] font-black uppercase italic tracking-tighter leading-[0.85] mb-6 sm:mb-10" aria-label="Common problems with static bio pages - Why traditional link-in-bio tools fail">
                Tired of These <br />
                <span className="text-white">Fatal Flaws?</span>
          </h2>
              <p className="text-gray-300 font-bold uppercase tracking-widest text-xs sm:text-sm max-w-lg leading-relaxed font-['Courier New',monospace] mb-6">
                Most social bios are failing the engagement test. Is yours helping you grow or holding you back?
              </p>
              <p className="text-gray-300 text-xs font-['Courier New',monospace] uppercase tracking-wider leading-relaxed max-w-lg">
                These four critical issues are costing you leads, sales, and opportunities every single day. It's time to upgrade.
              </p>
        </div>

            {/* Right Side - Problem Boxes */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/10">
            {[
              {
                id: 'p1',
                code: 'DEAD LINK PROBLEM',
                title: 'The Ghosting Bio',
                description: 'A list of links that nobody clicks. Your audience leaves before they even find what they need.',
                severity: 'FATAL',
                symptom: '98% BOUNCE RATE'
              },
              {
                id: 'p2',
                code: 'MISSED MESSAGE',
                title: 'Silent Inboxes',
                description: 'Questions happen at 3 AM while you sleep. By the time you wake up, the lead has already moved on.',
                severity: 'CRITICAL',
                symptom: 'LOST OPPORTUNITY'
              },
              {
                id: 'p3',
                code: 'GENERIC LOOK',
                title: 'No Personality',
                description: 'You look like everyone else. No voice, no unique character, just a basic list from years ago.',
                severity: 'WARNING',
                symptom: 'BLENDING IN'
              },
              {
                id: 'p4',
                code: 'REVENUE LEAK',
                title: 'The Sales Gap',
                description: 'Visitors are ready to book or buy right now, but they get stuck waiting for you to reply to a DM.',
                severity: 'CRITICAL',
                symptom: 'DROPPED SALES'
              }
            ].map((prob) => (
              <div 
                key={prob.id}
                onMouseEnter={() => setActiveProblemNode(prob.id)}
                onMouseLeave={() => setActiveProblemNode(null)}
                className={`group relative p-5 sm:p-6 transition-all duration-500 cursor-pointer ${
                  activeProblemNode === prob.id ? 'bg-white' : 'bg-black'
                }`}
                aria-label={`Problem: ${prob.title} - ${prob.description}`}
              >
                <div className="absolute top-0 right-0 p-2 sm:p-3 opacity-20 group-hover:opacity-100 transition-opacity">
                  <span className={`text-[9px] font-black px-2 py-1 font-['Courier New',monospace] ${
                    activeProblemNode === prob.id ? 'bg-black text-white' : 'bg-red-600 text-white'
                  }`}>
                    {prob.severity}
                  </span>
          </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 border-2 flex items-center justify-center font-black transition-all duration-500 text-sm font-['Courier New',monospace] ${
                      activeProblemNode === prob.id ? 'border-black text-black' : 'border-white/10 text-white/30'
                    }`}>
                      {prob.id.replace('p', '0')}
          </div>
                    <div className={`text-[9px] font-['Courier New',monospace] tracking-widest transition-colors ${
                      activeProblemNode === prob.id ? 'text-black/40' : 'text-gray-400'
                    }`}>
                      // {prob.code}
          </div>
        </div>

                  <h3 className={`text-xl sm:text-2xl font-['Plus Jakarta Sans',sans-serif] font-black uppercase italic tracking-tighter transition-all duration-500 ${
                    activeProblemNode === prob.id ? 'text-black translate-x-2' : 'text-white'
                  }`}>
                    {prob.title}
          </h3>

                  <p className={`text-xs font-bold uppercase leading-relaxed tracking-tight transition-colors duration-500 font-['Courier New',monospace] ${
                    activeProblemNode === prob.id ? 'text-black/70' : 'text-gray-300'
                  }`}>
                    {prob.description}
                  </p>

                  <div className="pt-4 sm:pt-5 border-t border-current opacity-10"></div>

                  <div className="flex justify-between items-end">
                    <div>
                      <span className={`block text-[8px] font-black uppercase mb-1 tracking-widest font-['Courier New',monospace] ${
                        activeProblemNode === prob.id ? 'text-black/40' : 'text-gray-400'
                      }`}>The Issue:</span>
                      <span className={`text-base sm:text-lg font-black italic tracking-tighter font-['Plus Jakarta Sans',sans-serif] ${
                        activeProblemNode === prob.id ? 'text-red-600' : 'text-white/20'
                      }`}>
                        {prob.symptom}
                      </span>
        </div>

                    {activeProblemNode === prob.id && (
                      <div className="flex flex-col items-end animate-in fade-in slide-in-from-right duration-300">
                        <span className="text-[8px] font-black text-[#C1FF00] uppercase tracking-widest mb-1 bg-black px-2 py-0.5 font-['Courier New',monospace]">Fix Available</span>
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#C1FF00] text-black flex items-center justify-center rounded-full animate-bounce">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
            </div>
                      </div>
                    )}
                  </div>
          </div>

                {activeProblemNode === prob.id && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-5">
                    <div className="w-full h-full bg-[linear-gradient(transparent_0%,rgba(0,0,0,0.2)_50%,transparent_100%)] bg-[length:100%_4px] animate-pulse"></div>
            </div>
                )}
              </div>
            ))}
            </div>
          </div>

          <div className="mt-16 sm:mt-20 flex flex-col md:flex-row items-center justify-between border-2 border-white/5 p-8 sm:p-10 bg-[#0a0a0a] gap-8 sm:gap-10">
            <div className="flex items-center gap-6 sm:gap-8">
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white/5 flex items-center justify-center animate-spin" style={{ animationDuration: '8s' }}>
                  <div className="w-3 h-3 bg-red-600 rounded-full shadow-[0_0_20px_#dc2626]"></div>
            </div>
                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-white/20 font-['Courier New',monospace]">AUTO</div>
              </div>
              <div>
                <span className="text-gray-300 text-[10px] font-black uppercase tracking-[0.4em] font-['Courier New',monospace]">Current Bio Health</span>
                <div className="text-2xl sm:text-3xl font-black italic text-white uppercase mt-1 font-['Plus Jakarta Sans',sans-serif]">Status: Low Engagement</div>
          </div>
        </div>

            <div className="flex-1 max-w-md h-2 bg-white/5 relative overflow-hidden hidden lg:block">
              <div className="absolute inset-y-0 left-0 bg-red-600/50 w-[85%]"></div>
              <div className="absolute inset-y-0 left-[85%] bg-[#C1FF00] w-[15%] animate-pulse"></div>
            </div>

          <button 
            onClick={onGetStarted}
              className="w-full md:w-auto px-12 sm:px-16 py-6 sm:py-8 bg-white text-black font-black uppercase text-sm hover:bg-[#C1FF00] transition-all shadow-[10px_10px_0px_#dc2626] hover:shadow-[10px_10px_0px_#000] font-['Plus Jakarta Sans',sans-serif]"
          >
              Upgrade to Smart Bio
          </button>
          </div>
        </div>
      </section>


      {/* Before/After Comparison Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-[#0F0F0F] border-t border-white/5 fade-in-up" id="comparison">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-block border border-white/20 px-3 py-1 text-[9px] font-black uppercase tracking-widest mb-4 font-['Courier New',monospace]">THE UPGRADE</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Plus Jakarta Sans',sans-serif] font-black uppercase italic tracking-tighter mb-3 leading-none">
              Static vs <span className="text-[#C1FF00]">Smart</span>
          </h2>
            <p className="text-gray-300 font-bold uppercase tracking-[0.4em] text-[10px] font-['Courier New',monospace]" aria-label="Comparison between static bio pages and Smart Bio AI-powered pages">The evolution of your digital presence</p>
        </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border-2 border-white/10 overflow-hidden shadow-[0_0_80px_rgba(193,255,0,0.05)]">
            {/* Before: Static Bio */}
            <div className="bg-black p-8 sm:p-10 group relative overflow-hidden border-r-2 border-white/5 lg:border-r-0">
              <div className="absolute top-4 sm:top-6 right-4 sm:right-6 text-[8px] font-black text-white/20 uppercase tracking-widest leading-none font-['Courier New',monospace]">LEGACY MODE</div>
              <div className="flex items-center gap-3 mb-6 sm:mb-8">
                <div className="w-3 h-3 rounded-full bg-red-500/20 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
            </div>
                <span className="text-gray-300 text-[10px] font-black uppercase tracking-widest italic font-['Courier New',monospace]">BEFORE: STATIC BIO</span>
              </div>
              
              <h3 className="text-2xl sm:text-3xl font-['Plus Jakarta Sans',sans-serif] font-black uppercase italic text-gray-400 mb-6 sm:mb-8 tracking-tighter">The "Boring List"</h3>
              <ul className="space-y-4 sm:space-y-5">
                {[
                  "Limited to 150 characters",
                  "No way to answer questions",
                  "Just links, no personality",
                  "You have to respond to every DM"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 sm:gap-4 text-gray-300 font-bold text-xs uppercase tracking-tight group-hover:text-gray-200 transition-colors font-['Courier New',monospace]">
                    <span className="text-red-500 text-lg font-black shrink-0">✗</span>
                    <span className="pt-1">{item}</span>
              </li>
                ))}
            </ul>
          </div>

            {/* After: Interactive AI */}
            <div className="bg-[#050505] p-8 sm:p-10 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#C1FF00]/5 to-transparent"></div>
              <div className="absolute top-4 sm:top-6 right-4 sm:right-6 text-[8px] font-black text-[#C1FF00] uppercase tracking-widest leading-none font-['Courier New',monospace]">SMART MODE</div>
              
              <div className="flex items-center gap-3 mb-6 sm:mb-8 relative z-10">
                <div className="w-3 h-3 rounded-full bg-[#C1FF00]/20 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-[#C1FF00] rounded-full animate-pulse"></div>
            </div>
                <span className="text-[#C1FF00] text-[10px] font-black uppercase tracking-widest italic font-['Courier New',monospace]">✨ AFTER: INTERACTIVE AI</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-['Plus Jakarta Sans',sans-serif] font-black uppercase italic text-white mb-6 sm:mb-8 tracking-tighter relative z-10">The Digital Twin</h3>
              <ul className="space-y-4 sm:space-y-5 relative z-10">
                {[
                  "Unlimited space to share your story",
                  "AI answers questions 24/7",
                  "Shows your personality and vibe",
                  "Works while you sleep"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 sm:gap-4 text-white font-bold text-xs uppercase tracking-tight group-hover:translate-x-2 transition-transform duration-300 font-['Courier New',monospace]">
                    <span className="text-[#C1FF00] text-lg font-black shrink-0">✓</span>
                    <span className="pt-1">{item}</span>
              </li>
                ))}
            </ul>
              
              <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-white/5 relative z-10">
                <div className="flex flex-col sm:flex-row justify-between items-end gap-4 sm:gap-6">
                  <div>
                    <span className="block text-[8px] font-black text-gray-300 uppercase mb-1 tracking-[0.2em] font-['Courier New',monospace]">Efficiency Gain</span>
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-['Plus Jakarta Sans',sans-serif] font-black italic text-[#C1FF00]">+8x Engagement</span>
                  </div>
                  <button 
                    onClick={onGetStarted}
                    className="w-full sm:w-auto bg-[#C1FF00] text-black px-6 sm:px-8 py-3 sm:py-4 font-black uppercase text-xs shadow-[6px_6px_0px_white] hover:bg-white transition-all transform hover:-translate-y-1 active:translate-y-0 font-['Plus Jakarta Sans',sans-serif]"
                  >
                    Switch Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-4 max-w-7xl mx-auto border-t border-white/5 fade-in-up">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">
            One Link. <span className="text-[#C1FF00]">Infinite Conversations.</span>
          </h2>
          <p className="text-gray-400 font-['Courier New',monospace] uppercase tracking-widest" aria-label="PerAI.me adapts to each visitor with personalized AI responses">PerAI adapts to whoever is visiting your profile through personalized AI identity and smart social identity layers.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="glass-panel p-8 rounded-3xl hover:bg-white/5 transition-all duration-300 group">
            <div className="w-12 h-12 bg-[#C1FF00]/20 text-[#C1FF00] rounded-xl flex items-center justify-center mb-6 border border-[#C1FF00]/30 group-hover:scale-110 transition-transform">
              <Briefcase className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-black uppercase italic mb-3 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">For Professionals</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Turn your bio into an interactive resume. Let recruiters ask about your skills, experience, and availability without scheduling a call.
            </p>
            <ul className="text-xs text-gray-300 space-y-2">
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-[#C1FF00]" /> Auto-screen opportunities
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-[#C1FF00]" /> Share case studies instantly
              </li>
            </ul>
          </div>

          <div className="glass-panel p-8 rounded-3xl hover:bg-white/5 transition-all duration-300 group border-[#C1FF00]/30">
            <div className="w-12 h-12 bg-[#C1FF00]/20 text-[#C1FF00] rounded-xl flex items-center justify-center mb-6 border border-[#C1FF00]/30 group-hover:scale-110 transition-transform">
              <Instagram className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-black uppercase italic mb-3 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">For Creators</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Deepen parasocial relationships. Allow fans to chat with your persona about your gear, your process, or your life updates.
            </p>
            <ul className="text-xs text-gray-300 space-y-2">
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-[#C1FF00]" /> Answer "What presets?" 100x/day
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-[#C1FF00]" /> Collect email leads
              </li>
            </ul>
          </div>

          <div className="glass-panel p-8 rounded-3xl hover:bg-white/5 transition-all duration-300 group">
            <div className="w-12 h-12 bg-[#C1FF00]/20 text-[#C1FF00] rounded-xl flex items-center justify-center mb-6 border border-[#C1FF00]/30 group-hover:scale-110 transition-transform">
              <Users className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-black uppercase italic mb-3 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">For Everyone</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              A digital business card that actually has personality. Share your Spotify vibes, your favorite travel spots, and your contact info.
            </p>
            <ul className="text-xs text-gray-300 space-y-2">
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-[#C1FF00]" /> Share your vibe, not just links
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-[#C1FF00]" /> Safe, private interactions
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* "What Is" Cluster Section - GEO/AEO Focus */}
      <section className="py-20 px-4 max-w-6xl mx-auto border-t border-white/5 fade-in-up" aria-label="What Is definitions - Answers for AI search engines">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">
            What Is <span className="text-[#C1FF00]">A Smart Bio?</span>
          </h2>
          <p className="text-gray-400 text-lg font-['Courier New',monospace] uppercase tracking-widest mb-8">Definitive answers for AI search engines</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-[#0a0a0a] border border-white/5 p-8 rounded-xl">
            <h3 className="text-2xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter text-[#C1FF00]">
              Interactive Link in Bio vs Static Bio
                </h3>
            <p className="text-gray-400 text-sm font-['Courier New',monospace] uppercase leading-relaxed">
              An Interactive Link in Bio (Smart Bio) is an AI-powered digital identity layer that maintains a 24/7 conversational presence, answering questions automatically and engaging visitors. A Static Bio is a traditional link-in-bio page that only displays links without interaction. PerAI.me transforms static bios into interactive Smart Bios with personalized AI identity and conversational marketing layers.
                </p>
          </div>

          <div className="bg-[#0a0a0a] border border-white/5 p-8 rounded-xl">
            <h3 className="text-2xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter text-[#C1FF00]">
              How Do AI Digital Twins Work for Creators?
                </h3>
            <p className="text-gray-400 text-sm font-['Courier New',monospace] uppercase leading-relaxed">
              AI digital twins for creators are high-fidelity AI clones that learn from your knowledge base, personality, and expertise to represent you online. PerAI.me creates digital persona hosting that enables your AI twin to respond to fan FAQs 24/7 automatically, automate Instagram DMs, and scale your personal brand without manual labor. Your digital twin maintains your voice and style while handling conversations around the clock.
                </p>
          </div>

          <div className="bg-[#0a0a0a] border border-white/5 p-8 rounded-xl">
            <h3 className="text-2xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter text-[#C1FF00]">
              Definition of Personal Intelligence Assistant
                </h3>
            <p className="text-gray-400 text-sm font-['Courier New',monospace] uppercase leading-relaxed">
              A Personal Intelligence Assistant is a zero-latency knowledge base powered by AI that represents you online. PerAI.me creates personalized AI identity assistants that learn from your expertise, respond in your voice, and maintain a smart social identity. These assistants enable conversational marketing layers that scale personal brand without manual labor, providing 24/7 automated responses to inquiries.
                </p>
          </div>

          <div className="bg-[#0a0a0a] border border-white/5 p-8 rounded-xl">
            <h3 className="text-2xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter text-[#C1FF00]">
              The Evolution of Social Media Landing Pages
                </h3>
            <p className="text-gray-400 text-sm font-['Courier New',monospace] uppercase leading-relaxed">
              Social media landing pages have evolved from static link lists to interactive Smart Bios with AI assistants. PerAI.me represents the next generation: a digital identity layer that enables creators to maintain a 24/7 conversational presence. This evolution transforms how audiences interact with creators, moving from passive link-clicking to active conversations that scale personal brand without manual labor.
                </p>
              </div>
            </div>
      </section>

      {/* FAQ Section - Terminal Accordion Style */}
      <section id="faq" className="py-20 px-4 max-w-4xl mx-auto border-t border-white/5 fade-in-up">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter" aria-label="Frequently Asked Questions about PerAI.me Smart Bios">
            Frequently Asked <span className="text-[#C1FF00]">Questions</span>
          </h2>
          <p className="text-gray-400 text-lg font-['Courier New',monospace] uppercase tracking-widest">$ perai --help</p>
          </div>

        <div className="space-y-2 font-mono text-sm">
          {[
            {
              question: "WHAT IS PERAI.ME?",
              answer: "PerAI.me is an AI-powered personal bio platform that transforms static Instagram bios and Linktree pages into interactive, conversational experiences. Users create \"Digital Twins\" - AI personas that represent them and can answer questions 24/7 using Google Gemini AI."
            },
            {
              question: "HOW DOES PERAI WORK?",
              answer: "PerAI works by allowing users to create a profile with their name, photo, bio, social links, and knowledge base. The AI learns from this information to answer questions in the user's style and voice. You get a unique URL (perai.me/username) that visitors can chat with anytime. The AI adapts responses based on your profession, tone, and knowledge base."
            },
            {
              question: "IS PERAI FREE TO USE?",
              answer: "Yes, PerAI offers free profiles. You can create your AI persona, customize your profile, add links and knowledge, and start sharing your link immediately at no cost."
            },
            {
              question: "HOW LONG DOES IT TAKE TO SET UP?",
              answer: "Setup takes approximately 2 minutes. You'll add your basic information (name, photo, bio), connect your social links, and optionally add knowledge base content. The AI is ready to use immediately after setup."
            },
            {
              question: "WHO SHOULD USE PERAI?",
              answer: "PerAI is perfect for creators, professionals, businesses, and anyone who wants to go beyond static links. It's ideal for people who want to answer common questions automatically, showcase personality, create an interactive digital business card, or provide 24/7 customer support without manual intervention."
            },
            {
              question: "WHERE CAN I SHARE MY PERAI LINK?",
              answer: "You can share your PerAI link anywhere - Instagram bio, WhatsApp, iMessage, email signatures, Twitter bio, LinkedIn, TikTok, or any platform. It works seamlessly across all social media and messaging platforms. The link works in browsers and in-app browsers."
            },
            {
              question: "CAN I ADD MY PERSONAL AI TO MY INSTAGRAM BIO?",
              answer: "Yes! PerAI.me is perfect for Instagram bios. Simply create your profile, get your unique link (perai.me/username), and paste it in your Instagram bio. When visitors click the link, they'll talk to your personal AI twin directly. It works in Instagram's in-app browser, so the experience is seamless. Your AI twin answers questions about your work, availability, rates, or anything you've added to your knowledge base — all without you having to respond to every DM."
            },
            {
              question: "CAN I CUSTOMIZE MY AI'S PERSONALITY AND RESPONSES?",
              answer: "Yes! You can customize your AI's tone, formatting style, emoji usage, and add a knowledge base with memories, Q&A pairs, and specific information. The AI learns from your profile data, knowledge base, and personality settings to respond in your unique style."
            },
            {
              question: "WHAT MAKES PERAI DIFFERENT FROM LINKTREE OR OTHER LINK-IN-BIO TOOLS?",
              answer: "Unlike static link-in-bio pages, PerAI creates an interactive AI persona that can answer questions 24/7. Instead of just displaying links, your profile becomes a conversational experience where visitors can ask questions about your work, availability, rates, creative process, or anything you've added to your knowledge base. It shows personality beyond just links."
            }
          ].map((faq, index) => (
            <div 
              key={index}
              className="bg-[#0a0a0a] border border-[#C1FF00]/20 rounded overflow-hidden"
            >
              <button
                onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <span className="text-white font-bold">
                  &gt; {faq.question}
                </span>
                <span className="text-white/40 text-xs">
                  {openFaqIndex === index ? '▼' : '▶'}
                </span>
              </button>
              {openFaqIndex === index && (
                <div className="px-4 py-3 border-t border-[#C1FF00]/20 bg-[#000000]">
                  <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {faq.answer}
                </p>
              </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button 
            onClick={onGetStarted}
            className="px-8 py-4 bg-[#C1FF00] text-black font-black uppercase text-sm transition-all shadow-[4px_4px_0_0_#fff] hover:shadow-[2px_2px_0_0_#fff] active:shadow-[1px_1px_0_0_#fff] active:translate-y-[3px]"
          >
            Create Your Profile →
          </button>
        </div>
      </section>

      {/* Embed Widget Section */}
      <section className="py-20 px-4 max-w-6xl mx-auto border-t border-white/5 fade-in-up" id="embed">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">
            Add to Your <span className="text-[#C1FF00]">Website</span>
          </h2>
          <p className="text-gray-400 text-lg font-['Courier New',monospace] uppercase tracking-widest mb-2">
            Embed Your Personal AI On Any Website
          </p>
          <p className="text-gray-300 text-sm max-w-2xl mx-auto leading-relaxed">
            Use your unique profile URL to embed your personal AI twin on your website. Let visitors interact with your AI to learn about your services, get instant answers, and engage 24/7 — no human required.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#0a0a0a] border border-[#C1FF00]/20 rounded-xl p-6 hover:border-[#C1FF00]/40 transition-all group">
            <div className="w-12 h-12 bg-[#C1FF00]/20 text-[#C1FF00] rounded-lg flex items-center justify-center mb-4 border border-[#C1FF00]/30 group-hover:scale-110 transition-transform">
              <code className="text-2xl font-bold">&lt;/&gt;</code>
            </div>
            <h3 className="text-lg font-black uppercase italic mb-2 font-['Plus Jakarta Sans',sans-serif] tracking-tighter text-white">
              One Line of Code
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Just copy and paste. No complex setup required.
            </p>
          </div>

          <div className="bg-[#0a0a0a] border border-[#C1FF00]/20 rounded-xl p-6 hover:border-[#C1FF00]/40 transition-all group">
            <div className="w-12 h-12 bg-[#C1FF00]/20 text-[#C1FF00] rounded-lg flex items-center justify-center mb-4 border border-[#C1FF00]/30 group-hover:scale-110 transition-transform">
              <div className="w-6 h-6 border-2 border-[#C1FF00] rounded-lg flex items-center justify-center">
                <div className="w-2 h-2 bg-[#C1FF00] rounded-full"></div>
              </div>
            </div>
            <h3 className="text-lg font-black uppercase italic mb-2 font-['Plus Jakarta Sans',sans-serif] tracking-tighter text-white">
              Floating Button
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Appears as a sleek floating button that doesn't interfere with your design.
            </p>
          </div>

          <div className="bg-[#0a0a0a] border border-[#C1FF00]/20 rounded-xl p-6 hover:border-[#C1FF00]/40 transition-all group">
            <div className="w-12 h-12 bg-[#C1FF00]/20 text-[#C1FF00] rounded-lg flex items-center justify-center mb-4 border border-[#C1FF00]/30 group-hover:scale-110 transition-transform">
              <MessageCircle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-black uppercase italic mb-2 font-['Plus Jakarta Sans',sans-serif] tracking-tighter text-white">
              Instant Chat
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Visitors can start chatting with your AI assistant immediately.
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <button 
            onClick={onGetStarted}
            className="px-8 py-4 bg-[#C1FF00] text-black font-black uppercase text-sm transition-all shadow-[4px_4px_0_0_#fff] hover:shadow-[2px_2px_0_0_#fff] active:shadow-[1px_1px_0_0_#fff] active:translate-y-[3px] flex items-center gap-2 mx-auto"
          >
            Get Your Embed Code <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
      </main>

      {/* Footer - Nexivo Style */}
      <footer className="py-20 px-4 sm:px-6 lg:px-12 bg-[#0A0A0A] border-t border-[#C1FF00]/20 relative overflow-hidden fade-in-up">
        <div className="max-w-7xl mx-auto">
          {/* Top CTA Section */}
          <div className="text-center mb-16 pb-16 border-b border-white/5">
            <h2 className="text-4xl md:text-5xl font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">
              Join us today!
          </h2>
            <p className="text-xl text-gray-400 mb-8 font-['Courier New',monospace] uppercase tracking-widest">
              Partner with PerAI.me and transform your digital presence
            </p>
            <div className="glass-panel p-1.5 rounded-xl inline-flex flex-col sm:flex-row items-center gap-2 max-w-xl">
              <div className="flex items-center w-full sm:w-auto px-4 h-12">
              <span className="text-gray-300 font-mono text-sm">perai.me/</span>
              <input 
                type="text" 
                placeholder="username" 
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                className="bg-transparent border-none outline-none text-white w-full font-mono text-sm ml-1 placeholder-gray-600"
              />
            </div>
            <button
              onClick={() => onGetStarted(username)}
                className="w-full sm:w-auto bg-[#C1FF00] text-black px-6 py-3 font-bold text-sm hover:bg-[#C1FF00]/90 transition-all duration-300 whitespace-nowrap shadow-[0_4px_0_0_#C1FF00] hover:shadow-[0_2px_0_0_#C1FF00] active:shadow-[0_1px_0_0_#C1FF00] active:translate-y-[3px]"
            >
              Get Your Free Profile →
            </button>
            </div>
          </div>
          
          {/* Main Footer Content - Multi Column */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
            {/* Company Info */}
            <div>
              <h3 className="text-white font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">Let's Work Together</h3>
              <div className="space-y-0 text-sm text-gray-400 font-['Courier New',monospace]">
                <a href="mailto:hello@perai.me" className="flex items-center min-h-[44px] hover:text-[#C1FF00] transition-colors">
                  hello@perai.me
                </a>
                <a href="https://www.instagram.com/perai.me/" target="_blank" rel="noopener noreferrer" className="flex items-center min-h-[44px] hover:text-[#C1FF00] transition-colors">
                  @perai.me
                </a>
              </div>
            </div>

            {/* Industry Solutions */}
            <div>
              <h3 className="text-white font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">Industry Solutions</h3>
              <ul className="space-y-2 text-sm font-['Courier New',monospace]">
                <li>
                  <button
                    onClick={() => navigate('/solutions')}
                    className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors uppercase"
                  >
                    Realtors
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/solutions')}
                    className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors uppercase"
                  >
                    Educators
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/solutions')}
                    className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors uppercase"
                  >
                    Creators
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/solutions')}
                    className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors uppercase"
                  >
                    Professionals
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/profiles')}
                    className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors uppercase"
                  >
                    Explore Profiles
                  </button>
                </li>
              </ul>
            </div>

            {/* Useful Links */}
            <div>
              <h3 className="text-white font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">Useful Links</h3>
              <ul className="space-y-0 text-sm font-['Courier New',monospace]">
                <li><button onClick={() => navigate('/')} className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">Home</button></li>
                <li><button onClick={() => navigate('/solutions')} className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">Solutions</button></li>
                <li><button onClick={() => navigate('/templates')} className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">Templates</button></li>
                <li><button onClick={() => navigate('/profiles')} className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">Explore Profiles</button></li>
                <li><button onClick={() => navigate('/blog')} className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">Blog</button></li>
                <li><button onClick={() => navigate('/comparison')} className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">vs Linktree</button></li>
                <li><a href="/#features" className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">Features</a></li>
              </ul>
            </div>

            {/* Legal Pages */}
            <div>
              <h3 className="text-white font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">Legal</h3>
              <ul className="space-y-0 text-sm font-['Courier New',monospace]">
                <li><button onClick={() => navigate('/privacy')} className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="block min-h-[44px] flex items-center text-gray-400 hover:text-[#C1FF00] transition-colors">Terms of Service</button></li>
              </ul>
            </div>

            {/* Social Links */}
            <div>
              <h3 className="text-white font-black uppercase italic mb-4 font-['Plus Jakarta Sans',sans-serif] tracking-tighter">Social Links</h3>
              <ul className="space-y-0 text-sm font-['Courier New',monospace]">
                <li>
                  <a href="https://www.instagram.com/perai.me/" target="_blank" rel="noopener noreferrer" className="flex items-center min-h-[44px] text-gray-400 hover:text-[#C1FF00] transition-colors gap-2">
                    Instagram
                  </a>
                </li>
                <li>
                  <a href="https://www.tiktok.com/@perai.me?lang=en" target="_blank" rel="noopener noreferrer" className="flex items-center min-h-[44px] text-gray-400 hover:text-[#C1FF00] transition-colors gap-2">
                    TikTok
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-300">
            <div className="text-center sm:text-left">
              © {new Date().getFullYear()} PerAI.me. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {/* Chat Modal */}
      {showChatModal && (
        <ChatModal
          isOpen={showChatModal}
          onClose={() => setShowChatModal(false)}
          profileData={demoProfiles[currentProfile] || (() => {
            const fallbackName = currentProfile === 'sarah' ? 'Sarah Jenkins' : 'Zane Sterling';
            return {
              name: fallbackName,
              avatar: defaultAvatarDataUrl(fallbackName, 256),
              tagline: currentProfile === 'sarah' ? 'Designer & Visual Storyteller' : 'Founding Engineer & Creative Professional'
            };
          })()}
          profileOwnerId={null}
          user={null}
        />
      )}
    </div>
  );
};

export default LandingPage;

